import React, { useEffect, useMemo, useState } from 'react';

import { useConfigStore, useAppStore, useMapInteractionStore } from '../../stores';
import { getAuthClient } from '../../utils/auth';
import { buildYourTourApiUrl } from '../../utils/ApiHelpers';
import { getDataVersion } from '../../utils/dataVersions';
import useAsyncEffect from '../../hooks/useAsyncEffect';
import { markFloorPlansVisited } from './prefetchFloorPlans';

import ResidenceFilter from '../ResidenceFilter/ResidenceFilter';
import FloorPlansPageList from './FloorPlansPageList';
import FloorPlansPageSkeleton from './FloorPlansPageSkeleton';
import MyLifeSiteLink from '../MyLifeSiteLink/MyLifeSiteLink';

import '../../GlobalScss/grid.scss';
import './FloorPlansPage.scss';

const FloorPlansPage = () => {
  const config = useConfigStore((state) => state.config);
  const featureFlags = useConfigStore((state) => state.featureFlags);
  const settings = useAppStore((state) => state.settings);
  const {
    enableMyLifeSite,
    myLifeSiteUrl,
    defaultLevelOfCare,
  } = config || {};
  const { platformShowSoldOutStatus, platformShowAvailableUnits } = featureFlags || {};
  const { urlSlug } = settings || {};

  const [items, setItems] = useState(null); // null indicates loading, [] means empty
  const [appliedSort, setAppliedSort] = useState('field_display_order'); // from API meta.sort
  const [error, setError] = useState(null);
  const filtersVisible = true;

  // Map interaction store - sync local page state to global store
  const activeCategoryFilter = useMapInteractionStore((state) => state.activeCategoryFilter);
  const activeFilterSelections = useMapInteractionStore((state) => state.activeFilterSelections);
  const activeLevelOfCareUuid = useMapInteractionStore((state) => state.activeLevelOfCareUuid);
  const resultFloorPlans = useMapInteractionStore((state) => state.resultFloorPlans);
  const validFloorPlanUuids = useMapInteractionStore((state) => state.validFloorPlanUuids);
  const preFormattedFilterData = useMapInteractionStore((state) => state.preFormattedFilterData);

  const setActiveCategoryFilter = useMapInteractionStore((state) => state.setActiveCategoryFilter);
  const setActiveFilterSelections = useMapInteractionStore((state) => state.setActiveFilterSelections);
  const setActiveLevelOfCareUuid = useMapInteractionStore((state) => state.setActiveLevelOfCareUuid);
  const setResultFloorPlans = useMapInteractionStore((state) => state.setResultFloorPlans);
  const setValidFloorPlanUuids = useMapInteractionStore((state) => state.setValidFloorPlanUuids);
  const setPreFormattedFilterData = useMapInteractionStore((state) => state.setPreFormattedFilterData);

  // Mark that user has visited floor plans page (prevents unnecessary prefetching)
  useEffect(() => {
    markFloorPlansVisited();
  }, []);

  // Initialize level of care from config on mount
  useEffect(() => {
    if (defaultLevelOfCare) {
      setActiveLevelOfCareUuid(defaultLevelOfCare);
    }
    // Cleanup on unmount - reset filter state
    return () => {
      setActiveCategoryFilter([]);
      setActiveFilterSelections({});
      setResultFloorPlans(null);
      setValidFloorPlanUuids([]);
      setPreFormattedFilterData(null);
    };
  }, [defaultLevelOfCare]);

  // Fetch floor plans data from optimized custom API endpoint
  // Inventory data is included in the response - no separate fetch needed
  useAsyncEffect(
    async (isMounted) => {
      if (!urlSlug) {
        return;
      }

      try {
        const auth = getAuthClient();
        const version = await getDataVersion( urlSlug, 'floorPlans' );
        const params = version ? { v: version } : {};
        const url = buildYourTourApiUrl( `/your_tour_api/floor_plans_page/${urlSlug}`, params );
        const response = await auth.fetchWithAuthentication(url, 'GET', null);

        const floorPlans = response?.data || [];
        const apiFilterData = response?.filterData || null;
        const sortFromApi = response?.meta?.sort || 'field_display_order';

        if (isMounted()) {
          setAppliedSort(sortFromApi);
          const isSquareFootageSort = sortFromApi === 'field_square_footage' || sortFromApi === '-field_square_footage';
          const sortedFloorPlans = isSquareFootageSort
            ? floorPlans
            : [...floorPlans].sort((a, b) => {
              const orderA = Number(a.displayOrder) || 0;
              const orderB = Number(b.displayOrder) || 0;
              if (orderA !== orderB) return orderA - orderB;
              return (a.title || '').localeCompare(b.title || '', undefined, { numeric: true, sensitivity: 'base' });
            });

          setItems(sortedFloorPlans);
          setPreFormattedFilterData(apiFilterData);
          setError(null);

          const floorPlanUuids = sortedFloorPlans.map((fp) => fp.uuid);
          setValidFloorPlanUuids(floorPlanUuids);
        }
      } catch (err) {
        // Only update state if component is still mounted
        if (isMounted()) {
          setError(err);
          setItems([]);
        }
      }
    },
    [urlSlug],
  );

  // Memoized filtered and sorted results
  // Show floor plans filtered by level of care immediately (don't wait for ResidenceFilter)
  const filteredItems = useMemo(() => {
    if (items === null) return [];

    let result = items;

    // Filter by level of care if set (happens immediately on load).
    // The floor_plans_page endpoint returns level of care as a multi-value
    // array (levelOfCareIds); tolerate a legacy singular levelOfCareId too.
    if (activeLevelOfCareUuid) {
      result = items.filter((item) => (
        Array.isArray(item.levelOfCareIds)
          ? item.levelOfCareIds.includes(activeLevelOfCareUuid)
          : item.levelOfCareId === activeLevelOfCareUuid
      ));
    }

    // Apply additional ResidenceFilter results if available (happens later)
    if (resultFloorPlans && resultFloorPlans.length > 0) {
      result = result.filter((item) => resultFloorPlans.indexOf(item.uuid) >= 0);

      if (appliedSort === 'field_square_footage' || appliedSort === '-field_square_footage') {
        return result;
      }
      return [...result].sort((a, b) => {
        const orderA = Number(a.displayOrder) || 0;
        const orderB = Number(b.displayOrder) || 0;
        if (orderA !== orderB) return orderA - orderB;
        return (a.title || '').localeCompare(b.title || '', undefined, { numeric: true, sensitivity: 'base' });
      });
    }

    return result;
  }, [items, resultFloorPlans, activeLevelOfCareUuid, appliedSort]);

  // Show skeleton loading ONLY while floor plans data is being fetched
  // Show the actual filter component so "Loading..." message is visible
  if (items === null && !error) {
    return (
      <div className="content floorPlansPage">
        <div className="floorPlansPage__filter">
          <ResidenceFilter
            showByFieldName="field_display_on_floor_plans_pag"
          />
        </div>
        <FloorPlansPageSkeleton />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="content">
        <p>Unable to load floor plans. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="content floorPlansPage">

      <div className="floorPlansPage__filter">
        <ResidenceFilter
          showByFieldName="field_display_on_floor_plans_pag"
        />
      </div>

      <FloorPlansPageList
        items={filteredItems}
        showSoldOutStatus={platformShowSoldOutStatus}
        showAvailableUnits={platformShowAvailableUnits}
      />

      {enableMyLifeSite && myLifeSiteUrl && (
        <MyLifeSiteLink
          url={myLifeSiteUrl}
        />
      )}

    </div>
  );
};

export default FloorPlansPage;
